import React, { useState } from "react";
import { Container, Typography, Divider, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useForm, Controller } from 'react-hook-form';
import { IconMail, IconPhone, IconBrandWhatsapp, IconBrandWechat, IconBrandLine, IconBrandYoutube, IconBrandFacebook, IconBrandInstagram } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";

import { useSnackbar } from "@context/snackbarContext";

import CustomTextField from "@components/inputs/customTextField";

import LogoVertical from "@assets/logo/zhushan-vertical-white-logo.png"

import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import InfoDialog from "@components/dialog/infoDialog";

import { UserApiEndpoints } from "@configuration/apiEndpoints";

import "./footer.css";

export default function Footer({ routes }) {
  const { t } = useTranslation()  
  const openSnackbar = useSnackbar()

  const { handleSubmit, control, getValues, reset, watch } = useForm({
    defaultValues: {
      emailAddress: "",
    }
  })

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [infoDialogConfig, setInfoDialogConfig] = useState({
    open: false,
    dialogTitle: t('subscribedNewsletter'),
    dialogDescription: t('subscribedNewsletterDesc'),
  })

  const subscribeMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.subscribeNewsletter}`, data, { requiresAuth: false })
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      setInfoDialogConfig({
        open: true,
        dialogTitle: t('subscribedNewsletter'),
        dialogDescription: t('subscribedNewsletterDesc'),
      })
      reset()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if(err?.response?.data === "Duplicate")
        setInfoDialogConfig({
          open: true,
          dialogTitle: t('alreadySubscribed'),
          dialogDescription: t('alreadySubscribedDesc'),
        })
      else
        openSnackbar(t('subscribeFailed'), "error");
      // if (err?.response) openSnackbar(err?.response?.data, "error");
      // else openSnackbar(t('subscribeFailed'), "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues()
    subscribeMutation.mutate({...data, by: "user"})
  }

  const watchEmailAddress = watch('emailAddress')

  return (
    <>
      <footer
        className="footer-container"
      >
        <Container 
          maxWidth="xl" 
        >
          <div className="content-container">
            <img src={LogoVertical} width={80} style={{objectFit: 'contain'}} alt='Zhu Shan'/>
            <div>
              <Typography className="section-title">{t('contact-page.zhushan')}</Typography>
              <div className='footer-point-warpper'>
                {/* <div className='point-inner'>
                  <div style={{flexShrink: 1, display: 'flex',alignSelf: 'center'}}>
                    <IconMapPin size={18} color='#FFF'/>
                  </div>
                  <Typography className='point'>
                    9, Jalan Kenari 13a, Bandar Puchong Jaya, 47170 Selangor
                  </Typography>
                </div> */}
                <div className='footer-point-inner'>
                  <div style={{flexShrink: 1, display: 'flex',alignSelf: 'center'}}>
                    <IconMail size={18} color='#FFF'/>
                  </div>
                  <Typography className='point'>
                    zhushan.mychan@gmail.com
                  </Typography>
                </div>
                <div className='footer-point-inner'>
                  <div style={{flexShrink: 1, display: 'flex',alignSelf: 'center'}}>
                    <IconPhone size={18} color='#FFF'/>
                  </div>
                  <Typography className='point'>
                    +60 17-204 3340
                  </Typography>
                </div>
              </div>
            </div>
            <div>
              <Typography className="section-title" marginBottom={'15px'}>{t('subscribeUs')}</Typography>
              <Controller
                name={`emailAddress`}
                control={control}
                rules={{
                  required: t('register-page.emailAddressRequired'),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: t('register-page.emailAddressInvalid'),
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField 
                    {...field}
                    placeholder={t('enterEmail')}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' } ,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: '#FFF',
                        borderRadius: "40px",
                        paddingRight: "0px",
                        '&:hover fieldset': {
                          borderColor: 'inherit',
                          borderWidth: 0
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'inherit',
                          borderWidth: 0,
                        },
                      },
                    }}
                    error={error}
                    helperText={error?.message}
                    endAdornment={
                      <CustomButton 
                        sx={{
                          borderRadius: 100,
                          height: "36px",
                          marginRight: "2px",
                        }}
                        color="#BBD0D3"
                        onClick={handleSubmit(onSubmit)}
                      >
                        {t('subscribe')}
                      </CustomButton>
                    }
                  />
                )}
              />
              <div className="social-button-wrapper">
                <Tooltip title="WhatsApp" placement="top">
                <IconButton component={RouterLink} to="https://wa.link/qkbxbj" target="_blank" rel="noopener noreferrer" aria-label="whatsapp" size="medium" sx={{ border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandWhatsapp size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
                <Tooltip title="WeChat" placement="top">
                <IconButton component={RouterLink} to="https://u.wechat.com/kO6JzSeEYxgACLiw6RybZ_A" target="_blank" rel="noopener noreferrer" aria-label="wechat" size="medium" sx={{  border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandWechat size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
                <Tooltip title="Line" placement="top">
                <IconButton component={RouterLink} to="https://line.me/ti/p/ht-fN3iRZh" target="_blank" rel="noopener noreferrer" aria-label="line" size="medium" sx={{  border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandLine size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
                <Tooltip title="YouTube" placement="top">
                <IconButton component={RouterLink} to="https://www.youtube.com/@ZhuShanFoundation" target="_blank" rel="noopener noreferrer" aria-label="line" size="medium" sx={{  border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandYoutube size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
                <Tooltip title="Facebook" placement="top">
                <IconButton component={RouterLink} to="https://www.facebook.com/ZhuShanFoundation" target="_blank" rel="noopener noreferrer" aria-label="line" size="medium" sx={{  border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandFacebook size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
                <Tooltip title="Instagram" placement="top">
                <IconButton component={RouterLink} to="https://www.instagram.com/zhushanfoundation" target="_blank" rel="noopener noreferrer" aria-label="line" size="medium" sx={{  border: '1px solid #BBD0D3', backgroundColor: '#BBD0D3', color: 'white', '&:hover': { backgroundColor: '#BBD0D3', borderColor: '#FFF' } }}>
                  <IconBrandInstagram size="20" color="#FFF"/>
                </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
          <Divider color="#FFF"/>
          <div className="footer-bottom">
            <Typography color="#FFFFFF" fontSize={'10px'} textAlign={'center'}>All Rights Reserved By ZHUSHAN FOUNDATION (202301015176)</Typography>
          </div>
        </Container>
      </footer>
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={onConfirmSubmit}
        loading={subscribeMutation.isPending}
        dialogTitle={t('subscribeConfirmTitle')}
        dialogDescription={t('subscribeConfirmDesc')}
        items={[watchEmailAddress]}
      />
      <InfoDialog
        open={infoDialogConfig.open}
        onClose={()=>setInfoDialogConfig(prev => ({...prev, open: false}))}
        dialogTitle={infoDialogConfig.dialogTitle}
        dialogDescription={infoDialogConfig.dialogDescription}
        closeText={t('ok')}
      />
    </>
  );
}